/**
 * Auth Callback - Handles OAuth redirect
 * Routes to appropriate handler based on auth provider
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { handleAuthCallback, USE_EMERGENT_AUTH } from "../auth";

export default function SupabaseAuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Capture and immediately clear signup intent marker to prevent stale false positives
      // This must happen BEFORE any success/failure branching
      const hadSignupIntent = sessionStorage.getItem('google_signup_intent') === 'true';
      sessionStorage.removeItem('google_signup_intent');

      // If using Emergent auth, the callback is already handled via cookies
      if (USE_EMERGENT_AUTH) {
        navigate("/dashboard", { replace: true });
        return;
      }

      // Handle Supabase OAuth callback
      const { success, user, error } = await handleAuthCallback();

      if (success && user) {
        // Google Ads signup conversion tracking - ONLY fire for true signup intent
        if (hadSignupIntent && window.gtag && !window.__signupTracked) {
          window.__signupTracked = true;
          window.gtag('event', 'conversion', {
            send_to: 'AW-18030156285/4sguCOqc0pIcEP2zuZVD',
            value: 1.0,
            currency: 'USD'
          });
        }
        toast.success(`Welcome, ${user.name}!`);
        
        if (user.role === "platform_admin") {
          navigate("/admin", { replace: true });
        } else if (user.first_value_onboarding?.active && user.first_value_onboarding?.redirect_to_job) {
          // First-value onboarding redirect for new users
          navigate(`/jobs/${user.first_value_onboarding.redirect_to_job}`, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        toast.error(error || "Authentication failed");
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-8">
      <div className="text-center">
        <div className="animate-pulse">
          <img
            src="https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png"
            alt="Loading..."
            className="max-w-sm w-full h-auto mx-auto object-contain opacity-50"
          />
        </div>
        <p className="mt-4 text-zinc-500 text-sm">Completing sign in...</p>
      </div>
    </div>
  );
}
